// The DM's zone painting over the board (issue 1149; design/30 §1.3). The canvas draws the zones (the DM's only, a tint
// a kind) and the box being dragged, and feeds the zone tool; this is where the kind is chosen — a fog bank, magical
// darkness, never revealed, difficult terrain when the system has a rule for it (issue 1151) — or Erase, and where a stroke is started as a new zone. Every change is one event through
// the table store; a player is sent nothing of a zone, only what it does to their sight.
import { useEffect, useState } from 'react';
import { ZONE_KINDS, ZONE_LABELS, type Zone } from '@dragons-table/vtt-shared';
import { useTableState } from '../table/TableState';
import { useMayAct } from '../table/mayAct';
import { ZONE_TINTS, zoneTool, type ZoneDraft, type ZoneMode } from './zoneTool';
import { pressWord } from '../shell/device';
import { BTN } from '../shell/ui';

const btn = BTN;

const hex = (n: number) => `#${n.toString(16).padStart(6, '0')}`;

const HINTS: Record<Exclude<ZoneMode, 'erase'>, string> = {
  fog_bank: 'A fog bank blocks sight as a wall does: nobody sees in or out across its edge.',
  darkness: 'Magical darkness blocks sight both ways; no light shines in it and darkvision does not see into it. Blindsight does.',
  never_revealed: 'Squares never revealed are never seen or explored by the players; only your reveal shows the map there.',
  difficult: 'Difficult terrain costs more movement, by the game system\'s rule; a move and the ruler count it. Players see it where they have explored.',
  sound: 'A sound area does nothing to sight or movement: tie a sound cue to it in Sound › Cues, and it plays when a token the players can see walks in.',
};
const zoneHint = (m: ZoneMode): string => (m === 'erase' ? `Drag a box, or ${pressWord().toLowerCase()} a square, to take it out of every zone.` : HINTS[m]);

function useZoneDraft(): ZoneDraft {
  const [d, setD] = useState(zoneTool.get());
  useEffect(() => zoneTool.subscribe(setD), []);
  return d;
}

/** A box released on the board becomes one event — the DM's own, never while looking through a player's eyes. */
export function ZoneGate() {
  const { act, role, viewingAs } = useTableState();
  const mayAct = useMayAct();
  // The zones are the DM's board's (`role`), and painting them the `map` power's (issue 1160).
  const dm = role === 'dm' && !viewingAs && mayAct('zone.paint');
  useEffect(() => { if (dm) return undefined; zoneTool.stop(); return zoneTool.subscribe((d) => { if (d.on) zoneTool.stop(); }); }, [dm]);
  useEffect(() => (dm ? zoneTool.onCommit((c) => act(c)) : undefined), [dm, act]);
  return null;
}

export function ZoneComposer() {
  const { act, role, viewingAs, store, systemRules } = useTableState();
  const mayAct = useMayAct();
  const d = useZoneDraft();
  // Difficult terrain is offered only when the system says what it costs (issue 1151; issue 1305's rule: no rule, no automation).
  const kinds = ZONE_KINDS.filter((k) => k !== 'difficult' || !!systemRules.distance?.difficult);
  // Escape leaves the tool from anywhere, as the fog tool's does (issue 1028).
  useEffect(() => {
    if (!d.on) return undefined;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') zoneTool.stop(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [d.on]);
  if (!d.on || role !== 'dm' || viewingAs || !mayAct('zone.paint')) return null;
  const zones = (store.state.zones ?? []) as Zone[];
  return (
    <div className="pointer-events-auto flex max-w-2xl flex-wrap items-center gap-2 self-start rounded bg-stone-900/95 px-3 py-2 text-xs text-stone-200" data-testid="zone-composer" role="group" aria-label="Paint zones">
      <span className="font-medium text-amber-300">Paint zones</span>
      <span role="radiogroup" aria-label="What a box paints" className="flex flex-wrap gap-0.5">
        {([...kinds, 'erase'] as ZoneMode[]).map((m) => (
          <button key={m} type="button" role="radio" aria-checked={d.mode === m} className={`${btn} flex items-center gap-1 ${d.mode === m ? 'border-amber-500 text-amber-300' : ''}`} onClick={() => zoneTool.mode(m)}>
            {m !== 'erase' && <span aria-hidden className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: hex(ZONE_TINTS[m]) }} />}
            {m === 'erase' ? 'Erase' : ZONE_LABELS[m]}
          </button>
        ))}
      </span>
      {d.mode !== 'erase' && <button type="button" className={btn} title="The next box starts a zone of its own instead of adding to the last one of this kind." onClick={() => zoneTool.fresh()}>New zone</button>}
      <span className="text-stone-400" data-testid="zone-count">{zones.length === 1 ? '1 zone on this map' : `${zones.length} zones on this map`}</span>
      <button type="button" className={btn} disabled={!zones.length} onClick={() => act({ kind: 'zone.remove', ids: zones.map((z) => z.id) })}>Remove all</button>
      <span className="text-stone-400" data-testid="zone-hint">{zoneHint(d.mode)} {d.mode === 'difficult' || d.mode === 'sound' ? '' : 'Zones matter with vision on.'}</span>
      <button type="button" className={btn} title="Leave the zone tool (Escape). Until then a drag on the board paints instead of moving a token." onClick={() => zoneTool.stop()}>Done</button>
    </div>
  );
}
